import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod/v4";
import { useId } from "react";
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "react-router";
import {
  data,
  Form,
  Link,
  redirect,
  useActionData,
  useSearchParams,
} from "react-router";
import { safeRedirect } from "remix-utils/safe-redirect";
import { z } from "zod";

import { Button } from "#guidelines/Form/Button";
import { Fieldset } from "#guidelines/Form/Fieldset";
import FormErrors from "#guidelines/Form/FormErrors";
import { Input } from "#guidelines/Form/Input";
import { AuthenticationLayout } from "#guidelines/Layout/AuthenticationLayout";
import { findUserIdentityInSession } from "#shared/Session.server";
import { createUser, getUserByEmail } from "#shared/User.server";

function isAllowedEmailProvider(email: string): boolean {
  return email.endsWith("@abric.net");
}

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  redirectTo: z.string().optional(),
});

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const userIdentity = await findUserIdentityInSession(request);
  if (userIdentity) {
    throw redirect("/");
  }

  return null;
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.clone().formData();
  const submission = await parseWithZod(formData, {
    async: true,
    schema: schema
      .refine(({ email }) => isAllowedEmailProvider(email), {
        path: ["email"],
        message: "Invalid email",
      })
      .refine(async ({ email }) => (await getUserByEmail(email)) === null, {
        path: ["email"],
        message: "An user already exists with this email",
      }),
  });

  if (submission.status !== "success") {
    return data(submission.reply(), {
      status: submission.status === "error" ? 400 : 200,
    });
  }

  const { email, password } = submission.value;

  await createUser(email, password);

  throw redirect(safeRedirect(submission.value.redirectTo, "/login"));
};

export const meta: MetaFunction = () => {
  return [
    {
      title: "Sign Up",
    },
  ];
};

export default function Join() {
  const lastResult = useActionData<typeof action>();
  const [searchParams] = useSearchParams();

  const formId = useId();

  const [form, { email, password, redirectTo }] = useForm({
    id: formId,
    defaultValue: {
      redirectTo: searchParams.get("redirectTo") ?? undefined,
    },
    lastResult,
    constraint: getZodConstraint(schema),
    onValidate({ formData }) {
      return parseWithZod(formData, { schema });
    },
  });

  return (
    <AuthenticationLayout title="Create an new account">
      <Form
        method="post"
        {...getFormProps(form)}
        className="space-y-4 md:space-y-6"
      >
        <Fieldset field={email} label="Email address">
          <Input
            {...getInputProps(email, { type: "email" })}
            autoComplete="email"
            autoFocus
          />
        </Fieldset>

        <Fieldset field={password} label="Password">
          <Input {...getInputProps(password, { type: "password" })} />
        </Fieldset>

        <input
          {...getInputProps(redirectTo, {
            type: "hidden",
            hidden: true,
          })}
        />

        <FormErrors errors={form.errors} />

        <Fieldset>
          <Button type="submit" className="btn-primary">
            Create account
          </Button>
        </Fieldset>

        <p className="text-sm font-light text-gray-500">
          Already have an account?{" "}
          <Link
            to={{
              pathname: "/login",
              search: searchParams.toString(),
            }}
            className="link link-accent"
          >
            Log in
          </Link>
        </p>
      </Form>
    </AuthenticationLayout>
  );
}
